/**
 * resolveStaleFragment.ts — PLAT-10266 (Card 2, epic PLAT-10209)
 *
 * The "Re-translate (machine)" resolve action for a STALE (amber) translated
 * fragment, plus the session-only resolved-lead bookkeeping that clears the
 * amber once a fragment is resolved. Pure of React/DOM — the review workspace
 * effect wires these to the amber affordance; the branch routing here is exactly
 * what the regression guard proves (DESIGN §7).
 *
 */
import {
  resolveVanishedSpanSource,
  type SourceBaselineEntry,
} from './editableOriginal';
import { collectSpanSourceElements } from './hooks/useSpanReplace';

import type {
  ConfirmDirectedFailureReason,
  ConfirmDirectedResult,
} from './hooks/useSpanReplace';
import type { SegmentFragmenter } from '@src/stores-v2/TranslationStore';
import type { TranscriptElement } from '@src/types.ts/transcript';

/** Which branch a stale-fragment re-translate took (DESIGN §7). */
export type RetranslateBranch = 'machine' | 'override';

export type RetranslateOutcome =
  | { ok: true; branch: RetranslateBranch }
  | { ok: false; branch: RetranslateBranch; reason: string };

/** The reasons the span editor's shipped notice already knows how to render. */
const EDITOR_FAILURE_REASONS: readonly ConfirmDirectedFailureReason[] = [
  'no-pending-span',
  'blocked-within-fix',
  'blocked-overlap',
  // PLAT-10945 renamed the paragraph refusal to a SPEAKER one (crossing a
  // paragraph is now allowed; crossing a speaker is not). Same slot, new name.
  'blocked-cross-speaker',
  'missing-source-element',
  'backend-failed',
  'author-failed',
];

/**
 * PLAT-10266 (2026-07-28) — map a FAILED outcome's reason onto the reason union the
 * span editor's existing notice renders (`DIRECTED_BLOCKED_TEXT` / `DIRECTED_ERROR_TEXT`
 * in `ReplaceSpanEditor`), so a refused re-translate reuses the shipped notice instead
 * of growing a second error surface.
 *
 * The MACHINE branch's reasons are already `ConfirmDirectedFailureReason` — they come
 * straight off `confirmDirected`. The OVERRIDE branch adds `update-failed`, which has no
 * counterpart: it means the in-place write did not land, so it maps to `author-failed`
 * ("nothing was authored or persisted"). An UNRECOGNISED reason maps there too — the
 * whole point of this card is that an unmapped reason must never become silent again.
 */
export function toEditorFailureReason(
  reason: string,
): ConfirmDirectedFailureReason {
  return EDITOR_FAILURE_REASONS.includes(reason as ConfirmDirectedFailureReason)
    ? (reason as ConfirmDirectedFailureReason)
    : 'author-failed';
}

export interface RetranslateMachineDeps {
  language: string;
  /** The stale fragment's lead — dropped from the amber set on success. */
  lead: number;
  /** The fragment's covered source indices (min..max span). */
  coveredIndices: readonly number[];
  /** The fragment's covered source element stable ids (drift-correct). */
  coveredElementIds: readonly string[];
  /**
   * `existingOverrideSegmentId` read from the OPENED span model
   * (`useSpanReplace.openForIndices` → `model.existingOverrideSegmentId`).
   * Undefined ⇒ a plain machine fragment; set ⇒ already an override.
   */
  existingOverrideSegmentId: string | undefined;
  /** The live scoped transcript array — the override branch's source run. */
  transcriptionElements: readonly TranscriptElement[];
  /**
   * PLAT-10266 (Card 7, §D) — the load-time source baseline (`captureSource
   * Baseline`). Used only as a FALLBACK: when the covered span no longer resolves
   * in `transcriptionElements` (a "Replace Term" deleted the covered token), the
   * override branch re-anchors on the baseline slot's added-token run so the
   * re-translate has a live source instead of aborting `missing-source-element`.
   * Undefined ⇒ no fallback (the pre-Card-7 behavior).
   */
  sourceBaseline?: ReadonlyMap<string, SourceBaselineEntry>;
  /** The injected splitter (undefined in the review today → a single fragment). */
  fragmenter: SegmentFragmenter | undefined;
  /**
   * Reuse the directed-span engine for a PLAIN machine fragment with an EMPTY
   * instruction (the overlap guard, null=failure, one-undo restore, coverage
   * kick, error notice — all reused; C6).
   */
  confirmDirected: (instruction: string) => Promise<ConfirmDirectedResult>;
  /** PURE value-getter — the fresh machine value for the override branch (C7). */
  directedRetranslateSpan: (
    lang: string,
    elements: TranscriptElement[],
    instruction: string,
  ) => Promise<string>;
  /** Land the fresh value IN PLACE on the existing override (NOT create; C8). */
  updateOverrideSegment: (
    lang: string,
    segmentId: string,
    value: string,
    options: { fragment?: boolean; fragmenter?: SegmentFragmenter },
  ) => Promise<boolean>;
  /** Drop the fragment's lead from the amber set (session only). On success. */
  onResolved: (lead: number) => void;
}

/**
 * Re-translate one STALE fragment from the machine, branching on whether it is
 * already an override (DESIGN §7). On success the fragment's lead is dropped
 * from the amber set via `onResolved` (re-translating the TARGET does NOT
 * un-edit the SOURCE word, so the edit-STATE staleness selector keeps returning
 * the lead — the amber must be cleared explicitly).
 */
export async function retranslateStaleFragment(
  deps: RetranslateMachineDeps,
): Promise<RetranslateOutcome> {
  const outcome =
    deps.existingOverrideSegmentId === undefined
      ? await retranslatePlainMachine(deps)
      : await retranslateOverrideInPlace(deps, deps.existingOverrideSegmentId);
  if (outcome.ok) deps.onResolved(deps.lead);
  return outcome;
}

/**
 * Plain machine fragment → reuse the directed-span engine with an EMPTY
 * instruction (C6). It authors a fresh `origin: 'directed'` override and brings
 * the overlap guard, `null=failure`, one-undo restore, coverage kick, and error
 * notice — all reused. We deliberately do NOT touch `createOverrideSegment` or
 * the manual path directly here.
 */
async function retranslatePlainMachine(
  deps: RetranslateMachineDeps,
): Promise<RetranslateOutcome> {
  const result = await deps.confirmDirected('');
  return result.ok
    ? { ok: true, branch: 'machine' }
    : { ok: false, branch: 'machine', reason: result.reason };
}

/**
 * Already-overridden fragment → `confirmDirected('')` REFUSES it
 * (`blocked-within-fix`, C6). NET-NEW path (Denys's decided option b): get the
 * fresh machine value via the PURE `directedRetranslateSpan` (authors nothing,
 * C7), then land it IN PLACE via `updateOverrideSegment` on the EXISTING
 * override (NOT `createOverrideSegment`, which would nest a second override, C8).
 * The source run is built by reusing the exported `collectSpanSourceElements`
 * (C10). The opts mirror the working `confirm` re-edit call site
 * (`{ fragment: !!fragmenter, fragmenter }`).
 */
async function retranslateOverrideInPlace(
  deps: RetranslateMachineDeps,
  segmentId: string,
): Promise<RetranslateOutcome> {
  let elements = collectSpanSourceElements(
    deps.transcriptionElements,
    deps.coveredElementIds,
    deps.coveredIndices,
  );
  if (elements === null) {
    // Card 7 (§D) — the covered span vanished (its source token was deleted by a
    // "Replace Term" edit). Fall back to the load-time baseline slot's added-token
    // run so we re-translate the replacement text instead of aborting. No baseline
    // (or no live replacement at the slot) ⇒ still a span we cannot place.
    const fallback = deps.sourceBaseline
      ? resolveVanishedSpanSource(
          deps.sourceBaseline,
          deps.transcriptionElements,
          deps.coveredElementIds,
          deps.coveredIndices,
        )
      : null;
    if (!fallback) {
      // A span we cannot place at all — never re-translate a DIFFERENT source.
      return {
        ok: false,
        branch: 'override',
        reason: 'missing-source-element',
      };
    }
    elements = fallback.sourceElements;
  }

  let value: string;
  try {
    value = await deps.directedRetranslateSpan(deps.language, elements, '');
  } catch {
    // `directedRetranslateSpan` throws on backend failure — author nothing.
    return { ok: false, branch: 'override', reason: 'backend-failed' };
  }
  // Mirror `confirmDirected`'s empty-result guard (REVIEW F2): never land a
  // blank re-translate in place.
  if (value.trim().length === 0) {
    return { ok: false, branch: 'override', reason: 'backend-failed' };
  }

  const ok = await deps.updateOverrideSegment(deps.language, segmentId, value, {
    fragment: !!deps.fragmenter,
    fragmenter: deps.fragmenter,
  });
  return ok
    ? { ok: true, branch: 'override' }
    : { ok: false, branch: 'override', reason: 'update-failed' };
}

/**
 * The amber set handed to the view = `computeStaleLeads(...)` MINUS the
 * session-only resolved leads (DESIGN §6/§7). Returns the SAME set reference
 * when nothing is resolved (a cheap identity so the memo below it stays stable),
 * and passes `undefined` through (no amber computed — e.g. the gated clip host).
 *
 * SUPERSEDED for the review wiring by `visibleStaleLeadsFromSignatures` (PLAT-10266
 * F1): a plain lead SET can only PERMANENTLY suppress a resolved lead, which both
 * pre-suppressed a fragment fixed while not stale and never re-flagged one on a
 * fresh source edit. Retained as a pure set util (its unit tests still hold).
 */
export function subtractResolvedLeads(
  staleLeads: ReadonlySet<number> | undefined,
  resolvedLeads: ReadonlySet<number>,
): ReadonlySet<number> | undefined {
  if (!staleLeads || resolvedLeads.size === 0) return staleLeads;
  const next = new Set<number>();
  for (const lead of staleLeads) {
    if (!resolvedLeads.has(lead)) next.add(lead);
  }
  return next;
}

/**
 * EDIT-KEYED amber set (PLAT-10266 F1). The amber handed to the view = the leads
 * `computeStaleSignatures` currently flags stale WHERE the resolved signature is
 * ABSENT (never resolved this session) OR DIFFERS from the current one (a fresh
 * source edit changed the covered span since the resolve → re-flag). Because it
 * only ever returns leads the selector already flags, a resolved fragment whose
 * source is unchanged stays hidden, but a re-edit re-lights it (DESIGN §6 "refresh
 * on a fresh edit"). Passes `undefined` through (gated clip host — no amber).
 */
export function visibleStaleLeadsFromSignatures(
  staleSignatures: ReadonlyMap<number, string> | undefined,
  resolvedSignatures: ReadonlyMap<number, string>,
): ReadonlySet<number> | undefined {
  if (!staleSignatures) return undefined;
  const next = new Set<number>();
  for (const [lead, signature] of staleSignatures) {
    if (resolvedSignatures.get(lead) !== signature) next.add(lead);
  }
  return next;
}

/**
 * Record a resolved fragment's CURRENT stale signature — but ONLY when the lead is
 * CURRENTLY stale (present in `staleSignatures`). A resolve of a genuinely-stale
 * fragment records its signature (so its amber clears immediately and stays cleared
 * until a fresh source edit). A span-confirm on a NOT-stale fragment (a normal
 * coverage "Fix Translation") records NOTHING, so it never pre-suppresses a future
 * first edit (PLAT-10266 F1, R3). Returns the SAME map reference when nothing
 * changed (a cheap identity for the memo above it). Session-only — held in React
 * state, so a reload re-baselines (R4).
 */
export function recordResolvedSignature(
  prev: ReadonlyMap<number, string>,
  lead: number,
  staleSignatures: ReadonlyMap<number, string> | undefined,
): ReadonlyMap<number, string> {
  const signature = staleSignatures?.get(lead);
  if (signature === undefined) return prev; // not currently stale → record nothing
  if (prev.get(lead) === signature) return prev; // already recorded, unchanged
  const next = new Map(prev);
  next.set(lead, signature);
  return next;
}
